import { Injectable } from "@angular/core";

import { TableDefinition, BfxColDef } from "./TableDefinition";
import { BlotterType, AssetClass } from "shared/cubiq-model-multi-asset";
import { ProfilePromiseActionService } from "../profile/impl/ProfilePromiseActionService";
import { ProfileSettingKeys } from "./core/ProfileSettingKeys";

import { BASE_TABLE_DEFINITION_VER } from "./TableDefinitionFactory.constants";
import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { TablePosition } from "./TablePosition";
import { ValueFormatterParams } from "ag-grid-community";
import { ProfilePrimaryActionService } from "../profile/impl/ProfilePrimaryActionService";
import { SystemConfigPrimaryService } from "shared/auth-services";
import { SysFeatures } from "./core/SysFeatures";

// OMS-30890 Per Keith D, "-" is reserved for empty numerical fields.
export const EMPTY_TEXT_VALUE: string = "";
export const EMPTY_AMT_RATE_VALUE: string = "-";

export function CALC_WS(numWs: number) {
  if (numWs <= 1) return 29;

  return 4 + numWs * 15;
}

export abstract class BaseTableDefinitionFactory {
  protected position: TablePosition;
  protected defaultColConfig: any = {};
  protected defaultSortModel: any = [];

  protected localeString: string = "DD-MMM-YY HH:mm:ss";

  // readonly DEFAULT_PROFILE: string = "userprofile/default/";

  // W valuse for column widths
  // note these W values include padding and spacing also
  protected oneWs: number = 29;
  protected twoWs: number = 34;
  protected threeWs: number = 49;
  protected fourWs: number = 64;
  protected fiveWs: number = 79;
  protected sixWs: number = 94;
  protected sevenWs: number = 109;
  protected eightWs: number = 124;
  protected nineWs: number = 139;
  protected tenWs: number = 154;
  protected elevenWs: number = 169;
  protected twelveWs: number = 184;
  protected thirteenWs: number = 199;
  protected fourteenWs: number = 214;
  protected fifteenWs: number = 229;
  protected sixteenWs: number = 244;
  protected seventeenWs: number = 259;
  protected eighteenWs: number = 274;
  protected nineteenWs: number = 289;
  protected twentyWs: number = 304;
  protected twentyFiveWs: number = 374;

  // Drop when we can use autoHeight
  protected minWrapEllipsisWs: number = 522;
  protected wrapEllipsisWs: number = 599;

  protected showUserName: boolean = false;

  constructor(protected profileService: ProfilePromiseActionService) {}

  protected getTableCongiPromises(): Array<Promise<any>> {
    return [];
  }

  protected handleTableCongiPromises(values: Array<any>) {
    //
  }

  protected getTableProfileConfig(): Promise<any> {
    return this.profileService.getProfileConfig(this.getProfileSettingKey());
  }

  protected handleTableProfileConfig(values: Array<any>): void {
    if (values[0]) {
      let defaultConfig = JSON.parse(values[0]);

      if (
        typeof defaultConfig === "object" &&
        defaultConfig !== null &&
        this.getVersionIdentifier() == defaultConfig.version
      ) {
        this.determineDefaultConfig(defaultConfig);
      } else {
        logger.out.warn("The Saved Default Blotter Config is incompatible, ignoring.");
      }
    }
  }

  protected getBlotterTypeStr(): string {
    return BlotterType[this.getBlotterType()];
  }
  protected getProfileSettingKey(): string {
    return `${ProfileSettingKeys.userProfileDefaultBlotter}${this.getBlotterTypeStr()}`;
  }

  protected determineDefaultConfig(defaultConfig: any) {
    if (defaultConfig.columnsConfiguration) {
      this.defaultColConfig = defaultConfig.columnsConfiguration;
    }
    if (defaultConfig.sortingModel) {
      this.defaultSortModel = defaultConfig.sortingModel;
    }
  }

  public defineTable(factoryContext: any = null): Promise<TableDefinition> {
    let profileService: any = this.profileService;
    let systemConfigService: SystemConfigPrimaryService = (
      profileService as ProfilePrimaryActionService
    ).systemSettingService;

    return new Promise<TableDefinition>((res, rej) => {
      Promise.all([
        this.getTableProfileConfig(),
        systemConfigService.getSystemConfig(SysFeatures.displayUserNames),
        ...this.getTableCongiPromises(),
      ])
        .then(
          (values) => {
            if (values[0]) {
              this.handleTableProfileConfig(values);
            }
            if (values[1]) {
              this.showUserName =
                values[1] == "true" ||
                values[1] == "TRUE" ||
                values[1] == "1" ||
                values[1] == "Y" ||
                values[1] == "y";
            }
            if (values.length > 2) {
              values.splice(0, 2);
              this.handleTableCongiPromises(values);
            }
            /////
            res(this.createDef(factoryContext));
          },
          (rejection) => {
            logger.out.error(
              "ERROR:OrdersTableDefinitionFactory rejection no sys configs returned, create table definition anyway:",
              rejection,
            );
            res(this.createDef(factoryContext));
          },
        )
        .catch((reason) => {
          logger.out.error(
            "ERROR:OrdersTableDefinitionFactory reason no sys configs returned, create table definition anyway: ",
            reason,
          );
          res(this.createDef(factoryContext));
        });
    });
  }

  protected valueOn(value: string): boolean {
    return value == "true" || value == "TRUE" || value == "1" || value == "Y" || value == "y";
  }

  public abstract getBlotterType(): BlotterType;
  public createDef(factoryContext: any = null): TableDefinition {
    this.position = new TablePosition();
    return null;
  }

  /**
   *
   * @returns
   */
  public getAssetClass(): AssetClass {
    return AssetClass.FIX;
  }

  public getVersionIdentifier(): string {
    return BASE_TABLE_DEFINITION_VER;
  }

  protected restoreFromConfig(colDef: BfxColDef): boolean {
    if (
      colDef &&
      this.defaultColConfig != null &&
      this.defaultColConfig[colDef.field] !== undefined
    ) {
      let colConfig = this.defaultColConfig[colDef.field];
      if (!colConfig) return false;
      if (typeof colConfig.width === "number" && colConfig.width >= 0)
        colDef.width = Number(colConfig.width);
      if (colConfig.visible !== undefined) colDef.hide = !colConfig.visible;
      if (colConfig.position !== undefined)
        if (colConfig.position < this.position.current()) {
          colDef.position = this.position.next();
        }

      colDef.position = colConfig.position;
      if (colConfig.isPrimary !== undefined) colDef.isPrimary = colConfig.isPrimary;
      if (colConfig.isPriority !== undefined) colDef.isPriority = colConfig.isPriority;

      return true;
    }
    return false;
  }

  public getDefaultSortingModel(): any {
    return this.defaultSortModel;
  }

  protected formatAmts(params: ValueFormatterParams): string {
    if (params.value) return params.value.toFormat(0);
    return EMPTY_AMT_RATE_VALUE;
  }

  protected formatBoolean(params: ValueFormatterParams): string {
    if (params.value != null) {
      if (params.value === true || params.value === 1) {
        return "Yes";
      } else {
        return "No";
      }
    }
    return EMPTY_TEXT_VALUE;
  }

  protected formatDate(format: string, params: ValueFormatterParams): string {
    if (params.value && params.value.local) return params.value.local().format(format);
    return EMPTY_TEXT_VALUE;
  }

  protected formatMomment(params: ValueFormatterParams): string {
    return this.formatDate(this.localeString, params);
  }

  protected formatStringAmt(params: ValueFormatterParams) {
    if (params.value) return params.value.replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
    return EMPTY_TEXT_VALUE;
  }

  protected formatPercent(params: ValueFormatterParams) {
    if (params.value && params.value.toPrecision) {
      return `${params.value.toFixed(0)}%`;
    }
    return EMPTY_AMT_RATE_VALUE;
  }

  /**
   * Limit the length of the string and append three dots at the end if
   * the length of the string is greater than the one provided in the argument.
   * Useful for large column titles.
   * @param value the string to be checked
   * @param length max length after which the string will be cut
   * @returns a string up to given length with three dots as a suffix
   */
  protected limitLength(value: string, length: number): string {
    if (value != null && length > 0 && value.length > length) {
      value = value.substring(0, length) + "...";
    }

    return value;
  }
}
